import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HomeIcon, CalculatorIcon, Menu, X, User, LogOut, Settings, CreditCard, LogIn, UserPlus, UserCircle } from 'lucide-react';
import UserMenu from './navigation/UserMenu';
import { useAuth } from '../contexts/AuthContext';
import { useAdmin } from '../hooks/useAdmin';
import Logo from './Logo';
import { useState, useEffect } from 'react';

const Navbar = () => {
  const { user, signOut } = useAuth();
  const { isAdmin } = useAdmin();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleQuickEstimate = () => {
    if (location.pathname === '/quick-estimate') {
      window.location.reload();
    } else {
      navigate('/quick-estimate');
    }
  };

  const NavItems = () => (
    <>
      <div className={`flex ${windowWidth < 768 ? 'flex-col space-y-3' : 'space-x-4'}`}>
        <button
          onClick={handleQuickEstimate}
          className="btn btn-secondary flex items-center justify-center"
        >
          <CalculatorIcon className="h-4 w-4 mr-2" />
          Estimation rapide
        </button>
        {user && (
          <Link
            to="/estimation/nouvelle"
            className="btn bg-orange-500 hover:bg-orange-600 text-white flex items-center justify-center"
          >
            Estimation détaillée
          </Link>
        )}
      </div>
    </>
  );

  return (
    <nav className="fixed top-0 left-0 right-0 bg-gradient-primary shadow-md z-50 h-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2 text-white hover:opacity-90">
              <HomeIcon className="h-6 w-6" />
              <Logo className="text-xl" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            <NavItems />
            <UserMenu />
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-white p-2 hover:bg-white/10 rounded-lg"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`
        fixed inset-0 bg-black/50 transition-opacity duration-300 md:hidden
        ${isMenuOpen ? 'opacity-100 z-40' : 'opacity-0 pointer-events-none'}
      `} onClick={() => setIsMenuOpen(false)}>
        <div
          className={`
            fixed right-0 top-16 bottom-0 w-80 bg-white shadow-xl
            transform transition-transform duration-300 ease-in-out
            ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}
          `}
          onClick={e => e.stopPropagation()}
        >
          <div className="p-6 space-y-6">
            {user && (
              <div className="flex items-center space-x-3 pb-4 border-b border-gray-200">
                <UserCircle className="h-8 w-8 text-primary" />
                <div>
                  <p className="font-medium text-gray-900">{user.email}</p>
                </div>
              </div>
            )}
            
            <div className="flex flex-col space-y-6">
              <NavItems />
              
              {user ? (
                <div className="space-y-4 pt-4 border-t border-gray-200">
                  <Link
                    to="/profile"
                    className="flex items-center px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <User className="h-5 w-5 mr-3 text-primary" />
                    Mon profil
                  </Link>
                  <Link
                    to="/dashboard"
                    className="flex items-center px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <CalculatorIcon className="h-5 w-5 mr-3 text-primary" />
                    Mes estimations
                  </Link>
                  <Link
                    to="/credits/purchase"
                    className="flex items-center px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <CreditCard className="h-5 w-5 mr-3 text-primary" />
                    Acheter des crédits
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="flex items-center px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Settings className="h-5 w-5 mr-3 text-primary" />
                      Administration
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      handleSignOut();
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg w-full text-left"
                  >
                    <LogOut className="h-5 w-5 mr-3" />
                    Déconnexion
                  </button>
                </div>
              ) : (
                <div className="space-y-4 pt-4 border-t border-gray-200">
                  <Link
                    to="/login"
                    className="flex items-center px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <LogIn className="h-5 w-5 mr-3 text-primary" />
                    Connexion
                  </Link>
                  <Link
                    to="/register"
                    className="flex items-center px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <UserPlus className="h-5 w-5 mr-3 text-primary" />
                    Inscription
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;